import { DialogComponent } from '@theme/dialog';

/**
 * Fullscreen, vertically swipeable reel viewer. Extends the theme's
 * DialogComponent for the fullscreen shell (scroll-lock, Escape/backdrop
 * close, native focus-trap) and adds: opening directly at a given reel
 * index, and playing only the reel currently snapped into view.
 *
 * Play/pause is driven by calling the public methods on each reel's
 * <deferred-media> element (assets/media.js): loadContent() (idempotent —
 * safe to call every time a reel becomes active, guarded internally by a
 * data-media-loaded attribute) and playMedia()/pauseMedia(). Closing the
 * dialog auto-pauses whatever's playing for free, since DeferredMedia
 * already listens for the dialog:close event globally.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} list
 *
 * @extends {DialogComponent}
 */
export class ReelsViewerComponent extends DialogComponent {
  requiredRefs = ['dialog', 'list'];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {number} */
  #activeIndex = -1;

  /**
   * Bound via `on:click="#reels-viewer/openAt?index=2"` from each preview
   * thumbnail in the horizontal row.
   * @param {{index?: number}} data
   * @param {Event} [event]
   */
  openAt(data, event) {
    event?.preventDefault();
    const index = Number(data?.index ?? 0);

    this.showDialog();

    // showDialog() schedules its own requestAnimationFrame to lock scroll
    // and call dialog.showModal(); wait a further two frames so the dialog
    // has taken up 100dvh before we measure it to scroll to the target reel.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#scrollToIndex(index, 'instant');
        this.#ensureObserver();
        this.#activateIndex(index);
      });
    });
  }

  /**
   * @param {number} index
   * @param {ScrollBehavior} behavior
   */
  #scrollToIndex(index, behavior) {
    const item = this.#items()[index];
    if (!item) return;
    item.scrollIntoView({ block: 'start', behavior });
  }

  /** @returns {HTMLElement[]} */
  #items() {
    return Array.from(this.refs.list.querySelectorAll('[data-reel-item]'));
  }

  #ensureObserver() {
    if (this.#observer) return;

    this.#observer = new IntersectionObserver(this.#handleIntersect, {
      root: this.refs.list,
      threshold: [0, 0.6, 1],
    });

    for (const item of this.#items()) {
      this.#observer.observe(item);
    }
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #handleIntersect = (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting || entry.intersectionRatio < 0.6) continue;

      const index = Number(/** @type {HTMLElement} */ (entry.target).dataset.index);
      if (Number.isNaN(index) || index === this.#activeIndex) continue;

      this.#activateIndex(index);
    }
  };

  /** @param {number} index */
  #activateIndex(index) {
    const items = this.#items();

    const previous = items[this.#activeIndex];
    previous?.querySelector('deferred-media')?.pauseMedia();

    this.#activeIndex = index;

    const current = items[index];
    const media = current?.querySelector('deferred-media');
    if (media) {
      media.loadContent();
      media.playMedia();
    }
  }
}

if (!customElements.get('reels-viewer-component')) {
  customElements.define('reels-viewer-component', ReelsViewerComponent);
}
